import React, { useState } from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { ItemSourceGuesserItemStatus as ItemStatus } from 'constants/itemSourceGuesserStatus';
import ItemSourceGuesserItemsModel from 'models/ItemSourceGuesserItemsModel';
import ModuleContext from 'CMFW/context/ModuleContext';
import { IItemSourceGuesserItem } from '../../../types/itemSourceGuesser';
import { useDispatch } from 'react-redux';
import { tabsOpened } from 'store/actions/tabs';
import { IRefIds } from 'constants/refs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAlignLeft, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import SplitButtonWithMenu, { MenuItemData } from '../../../components/common/SplitButtonWithMenu';

const ItemSourceGuesserActionRenderer: React.FC<ICellRendererParams> = (params) => {
  const data = params.data as IItemSourceGuesserItem;
  const itemsModel = React.useContext(ModuleContext) as ItemSourceGuesserItemsModel;
  const dispatch = useDispatch();
  const [processing, setProcessing] = useState<'keep' | 'change' | 'accept' | null>(null);

  if (!data) return null;
  if (data.status === ItemStatus.PENDING) return null;

  // If there's an error, show nothing in the action renderer
  if (data.error_message || data.ai_result?.decision === 'error') {
    return <ActionsContainer />;
  }

  const isKeep = data.decision === 'keep';
  const isChange = data.decision === 'change';
  const isAccept = data.decision === 'accept';
  const isDone = data.status === ItemStatus.DONE;
  const hasSelection = isDone;
  const isEmptyOriginal = !data.item.source || data.item.source.trim() === '';
  const aiDecision = data.ai_result?.decision;
  const aiSuggestions = Array.isArray(data.ai_result?.suggested_sources) ? data.ai_result!.suggested_sources : [];
  const hasAnyNonEmptySuggestion = aiSuggestions.some((s) => (s?.source || '').trim() !== '');

  // Convert suggestions to menu items format
  const menuItems: MenuItemData[] = aiSuggestions.map((suggestion, index) => ({
    label: suggestion.source,
    value: index,
    checked: suggestion.will_be_applied === true,
    badge: {
      text: `${Math.round(suggestion.confidence * 100)}%`,
      confidence: suggestion.confidence,
    },
  }));

  // Get default selected indices (suggestions with will_be_applied = true)
  const getDefaultSelectedIndices = (): number[] => {
    return aiSuggestions.map((s, idx) => (s.will_be_applied === true ? idx : -1)).filter((idx) => idx !== -1);
  };

  const handleKeep = async () => {
    if (processing || (isKeep && isDone)) return;
    setProcessing('keep');
    try {
      await itemsModel.keepItem(data.id);
    } finally {
      setProcessing(null);
    }
  };

  const handleChange = async (selectedIndices?: number[]) => {
    if (processing || (isChange && isDone)) return;
    setProcessing('change');
    try {
      const indices = selectedIndices ?? getDefaultSelectedIndices();
      await itemsModel.changeItem(data.id, indices);
    } finally {
      setProcessing(null);
    }
  };

  const handleAccept = async (selectedIndices?: number[]) => {
    if (processing || (isAccept && isDone)) return;
    setProcessing('accept');
    try {
      const indices = selectedIndices ?? getDefaultSelectedIndices();
      await itemsModel.acceptItem(data.id, indices);
    } finally {
      setProcessing(null);
    }
  };

  const handleChangeWithDefault = () => {
    handleChange(getDefaultSelectedIndices());
  };

  const handleAcceptWithDefault = () => {
    handleAccept(getDefaultSelectedIndices());
  };

  const handleViewSentences = () => {
    if (data.sentences_ids && data.sentences_ids.length > 0 && data.navigation_data) {
      const sentencesSearch = data.sentences_ids.join(',');
      dispatch(
        tabsOpened({
          refId: IRefIds.sentences,
          search: sentencesSearch,
          courseId: data.navigation_data.courseId,
        }),
      );
    }
  };

  const handleViewPanels = () => {
    if (data.panel_ids && data.panel_ids.length > 0 && data.navigation_data) {
      const panelsSearch = data.panel_ids.join(',');
      dispatch(
        tabsOpened({
          refId: IRefIds.panels,
          search: panelsSearch,
          courseId: data.navigation_data.courseId,
        }),
      );
    }
  };

  // When AI made no decision: show Change if there are suggestions; if all empty, show no actions
  if (aiDecision === 'no_decision') {
    if (!hasAnyNonEmptySuggestion) {
      return (
        <ActionsContainer>
          <ConceptRow>
            {data.sentences_ids && data.sentences_ids.length > 0 && (
              <ConceptButton
                onClick={handleViewSentences}
                title={`View ${data.sentences_ids.length} related sentences`}
              >
                <FontAwesomeIcon icon={faAlignLeft} />
              </ConceptButton>
            )}
            {data.panel_ids && data.panel_ids.length > 0 && (
              <ConceptButton onClick={handleViewPanels} title={`View ${data.panel_ids.length} related panels`}>
                <FontAwesomeIcon icon={faFileAlt} />
              </ConceptButton>
            )}
          </ConceptRow>
        </ActionsContainer>
      );
    }
    return (
      <ActionsContainer>
        <ButtonsRow>
          <SplitButtonWithMenu
            label="Accept"
            onClick={handleChangeWithDefault}
            variant="accept"
            selected={isChange && isDone}
            hasSelection={hasSelection}
            disabled={processing !== null}
            processing={processing === 'change'}
            menuItems={menuItems}
            onMenuItemsSelect={(indices) => handleChange(indices)}
          />
        </ButtonsRow>
        {(data.sentences_ids && data.sentences_ids.length > 0) || (data.panel_ids && data.panel_ids.length > 0) ? (
          <ConceptRow>
            {data.sentences_ids && data.sentences_ids.length > 0 && (
              <ConceptButton
                onClick={handleViewSentences}
                title={`View ${data.sentences_ids.length} related sentences`}
              >
                <FontAwesomeIcon icon={faAlignLeft} />
              </ConceptButton>
            )}
            {data.panel_ids && data.panel_ids.length > 0 && (
              <ConceptButton onClick={handleViewPanels} title={`View ${data.panel_ids.length} related panels`}>
                <FontAwesomeIcon icon={faFileAlt} />
              </ConceptButton>
            )}
          </ConceptRow>
        ) : null}
      </ActionsContainer>
    );
  }

  return (
    <ActionsContainer>
      <ButtonsRow>
        {!isEmptyOriginal && (
          <ActionButton
            onClick={handleKeep}
            disabled={processing !== null}
            $selected={isKeep && isDone}
            $hasSelection={hasSelection}
            $variant="keep"
            data-selected={isKeep && isDone ? 'true' : 'false'}
          >
            {processing === 'keep' ? <i className="fa fa-spinner fa-spin" /> : 'Keep'}
          </ActionButton>
        )}
        {!isEmptyOriginal && (
          <SplitButtonWithMenu
            label="Change"
            onClick={handleChangeWithDefault}
            variant="change"
            selected={isChange && isDone}
            hasSelection={hasSelection}
            disabled={processing !== null}
            processing={processing === 'change'}
            menuItems={menuItems}
            onMenuItemsSelect={(indices) => handleChange(indices)}
          />
        )}
        {isEmptyOriginal && (
          <SplitButtonWithMenu
            label="Accept"
            onClick={handleAcceptWithDefault}
            variant="accept"
            selected={isAccept && isDone}
            hasSelection={hasSelection}
            disabled={processing !== null}
            processing={processing === 'accept'}
            menuItems={menuItems}
            onMenuItemsSelect={(indices) => handleAccept(indices)}
          />
        )}
      </ButtonsRow>
      {(data.sentences_ids && data.sentences_ids.length > 0) || (data.panel_ids && data.panel_ids.length > 0) ? (
        <ConceptRow>
          {data.sentences_ids && data.sentences_ids.length > 0 && (
            <ConceptButton onClick={handleViewSentences} title={`View ${data.sentences_ids.length} related sentences`}>
              <FontAwesomeIcon icon={faAlignLeft} />
            </ConceptButton>
          )}
          {data.panel_ids && data.panel_ids.length > 0 && (
            <ConceptButton onClick={handleViewPanels} title={`View ${data.panel_ids.length} related panels`}>
              <FontAwesomeIcon icon={faFileAlt} />
            </ConceptButton>
          )}
        </ConceptRow>
      ) : null}
    </ActionsContainer>
  );
};

export default ItemSourceGuesserActionRenderer;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 5px;
  justify-content: center;
  align-items: center;
`;

const ConceptRow = styled(ButtonsRow)`
  margin-top: 4px;
  justify-content: right;
  width: 100%;
`;

const ActionButton = styled.button<{
  $selected?: boolean;
  $variant?: 'keep' | 'change' | 'accept';
  $hasSelection?: boolean;
  $isLeftButton?: boolean;
}>`
  padding: 6px 14px;
  background-color: ${(p) =>
    p.$selected
      ? p.$variant === 'keep'
        ? '#66BB6A'
        : p.$variant === 'change'
        ? '#42A5F5'
        : '#8E24AA'
      : p.$variant === 'keep'
      ? '#e8f5e9'
      : p.$variant === 'change'
      ? '#e3f2fd'
      : '#f3e5f5'};
  color: ${(p) =>
    p.$selected ? '#fff' : p.$variant === 'keep' ? '#2e7d32' : p.$variant === 'change' ? '#1565c0' : '#6a1b9a'};
  border: 1px solid
    ${(p) =>
      p.$selected
        ? 'transparent'
        : p.$variant === 'keep'
        ? '#81c784'
        : p.$variant === 'change'
        ? '#64b5f6'
        : '#ce93d8'};
  border-radius: ${(p) => (p.$isLeftButton ? '6px 0 0 6px' : '6px')};
  border-right: ${(p) => (p.$isLeftButton ? 'none' : '1px solid')};
  cursor: ${(p) => (p.$selected ? 'default' : 'pointer')};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${(p) => (p.$selected ? 1 : p.$hasSelection ? 0.3 : 1)};
  min-width: 74px;
  text-align: center;
  pointer-events: ${(p) => (p.$selected ? 'none' : 'auto')};
  &:hover:not(:disabled):not([data-selected='true']) {
    opacity: 1;
    background-color: ${(p) => (p.$variant === 'keep' ? '#66BB6A' : p.$variant === 'change' ? '#42A5F5' : '#8E24AA')};
    color: #fff;
  }
  &:disabled {
    cursor: wait;
    opacity: 0.5;
  }
`;

const ConceptButton = styled.button`
  padding: 6px 10px;
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 35px;

  &:hover {
    background-color: #1890ff;
    color: #fff;
    border-color: #1890ff;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;
